import { useEffect } from 'react'
import type { BillingPlanV2Tier } from '@highlight/common/types'
import { useModals } from '../../store'
import type { ModalObjectProps } from '../../types'
import { useUnifiedBilling } from '../../store/global-billing'
import { isPlanEnded, topUpTier } from '../../utils/billingUtils'
import { TopUpModal } from '@renderer/components/pricing/top-up-modal'
import { TOP_UP_SPECS, type TopUpKind } from '@renderer/components/pricing/top-up-kinds'
import { usePaywallShown } from '@renderer/components/pricing/use-top-up-actions'

export interface GatedTopUpModalContext {
  kind: TopUpKind
  /** The gate that opened it, for the Paywall analytics events. */
  from: string
}

/**
 * A top-up paywall opened by a billing gate or a credits CTA. The tier comes
 * from live billing state, and the modal closes itself once that state stops
 * naming one (the plan lapsed, or a refresh moved it to free).
 *
 * KNOWN WRONG: `atLimit` is pinned true and the Paywall event always reports
 * `limit-reached`, because this was written when the meeting-hours gate was the
 * only opener. Voluntary top-ups now arrive here too, so a user with headroom
 * reads "You've reached your plan limit" and the funnel counts them as blocked.
 * The fix is to take the real state the way subscription-settings does.
 */
export function GatedTopUpModal({ id, context }: ModalObjectProps) {
  const { closeModal } = useModals()
  const { paywall, subscription } = useUnifiedBilling()
  const { kind, from } = context as GatedTopUpModalContext
  const tier = topUpTier({ plan: paywall.plan, planEnded: isPlanEnded(subscription) })

  useEffect(() => {
    if (tier === null) {
      closeModal(id)
    }
  }, [tier, closeModal, id])

  if (tier === null) {
    return null
  }
  return <GatedTopUpModalBody id={id} kind={kind} from={from} tier={tier} />
}

function GatedTopUpModalBody({
  id,
  kind,
  from,
  tier,
}: {
  id: string
  kind: TopUpKind
  from: string
  tier: BillingPlanV2Tier
}) {
  const { closeModal } = useModals()
  const spec = TOP_UP_SPECS[kind]
  usePaywallShown({ tier, from }, spec.analytics.feature, 'limit-reached')

  return <TopUpModal spec={spec} tier={tier} from={from} atLimit onClose={() => closeModal(id)} />
}
